// ============================================================================
// BLOCK BP.UTIL.001 — Shared display formatting and value normalization
// -----------------------------------------------------------------------------
// PURPOSE
// - Centralize number/date/string formatting so Bank Posting screens render
//   the same child-row and invoice-list text across all apps.
// - Keep logic in one place to avoid multiple divergent formatters.
// ============================================================================

function money(value) {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
}

function formatDate(value) {
  if (!value) return '';

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [yyyy, mm, dd] = raw.split('-');
    return `${mm}-${dd}-${yyyy}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function trimStr(value) {
  return String(value ?? '').trim();
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function normalizeCustomerName(value) {
  return trimStr(value)
    .replace(/(?:\.)?\s*payables\b/gi, '')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/g, '')
    .trim();
}

// ============================================================================
// BLOCK BP.UTIL.002 — Child invoice classification and amount resolution
// -----------------------------------------------------------------------------
// PURPOSE
// - Decide whether a child row should render as a suggested invoice or a plain
//   open invoice.
// - Prefer invoice total/applied math when that data exists, so fallback rows do
//   not get forced down the open-only format before their real invoice math is seen.
// ============================================================================

function getAuthoritativeBalance(invoice) {
  const balance = Number(
    invoice?.sage_open_balance ??
      invoice?.open_invoice_balance ??
      invoice?.invoice_balance ??
      invoice?.open_balance ??
      invoice?.balance_due ??
      invoice?.amount_due ??
      invoice?.Balance ??
      invoice?.balance
  );

  return Number.isFinite(balance) ? balance : Number.NaN;
}

function hasAuthoritativeBalance(invoice) {
  return Number.isFinite(getAuthoritativeBalance(invoice));
}

function getPaymentAmount(invoice) {
  const candidates = [
    invoice?.applied_amount_override,
    invoice?.payment_amount,
    invoice?.applied_amount,
    invoice?.amount_applied,
    invoice?.invoice_amount,
  ];

  for (const value of candidates) {
    if (value === undefined || value === null || String(value).trim() === '') {
      continue;
    }

    const parsed = Number(value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
}

function hasMeaningfulInvoiceTotals(invoice) {
  const explicitOpenSignals =
    trimStr(invoice?.selected_source_kind).toLowerCase() === 'open' ||
    Boolean(invoice?.is_manual_open_assignment) ||
    trimStr(invoice?.invoice_source || invoice?.source)
      .toLowerCase()
      .startsWith('sage') ||
    trimStr(invoice?.match_origin || invoice?.match_reason)
      .toLowerCase()
      .startsWith('open');
  const balance = getAuthoritativeBalance(invoice);
  const appliedAmount = Number(invoice?.applied_amount);
  const paymentAmount = getPaymentAmount(invoice);
  const hasOpenBalance = balance > 0.005;
  const hasRealInvoiceData = [
    paymentAmount,
    appliedAmount,
    Number(invoice?.original_amount),
    Number(invoice?.amount),
    Number(invoice?.total_amount),
  ].some((value) => Number.isFinite(value) && value > 0.005);

  // Placeholder open rows are just the Sage open balance itself, so they should stay in the
  // open format even when the row has an invoice-like number. A real short-pay row has a lower
  // LOKA invoice amount than the Sage balance, which should render as a suggested invoice total.
  if (explicitOpenSignals && hasOpenBalance && hasRealInvoiceData) {
    const isPlaceholderOpen =
      Math.abs(balance - paymentAmount) < 0.005 && Math.abs(balance - appliedAmount) < 0.005;

    if (isPlaceholderOpen) {
      return false;
    }
  }

  return hasRealInvoiceData;
}

function resolveChildInvoiceKind(invoice) {
  const explicitKind = trimStr(invoice?.selected_source_kind).toLowerCase();
  const source = trimStr(invoice?.invoice_source || invoice?.source).toLowerCase();
  const origin = trimStr(invoice?.match_origin || invoice?.match_reason).toLowerCase();
  const hasMeaningfulInvoiceData = hasMeaningfulInvoiceTotals(invoice);
  const hasOpenPlaceholderSignals =
    explicitKind === 'open' ||
    Boolean(invoice?.is_manual_open_assignment) ||
    source.startsWith('sage') ||
    origin.startsWith('open');

  if (explicitKind === 'suggested') {
    return 'suggested';
  }

  if (hasMeaningfulInvoiceData) {
    return 'suggested';
  }

  if (hasOpenPlaceholderSignals) {
    return 'open';
  }

  return 'suggested';
}

function resolveChildInvoiceAmounts(invoice) {
  const balance = getAuthoritativeBalance(invoice);
  const paymentAmount = getPaymentAmount(invoice);
  const total = Number.isFinite(balance) ? balance : 0;

  const discountOverride = invoice?.invoice_discount_override;
  const discount =
    discountOverride !== undefined &&
    discountOverride !== null &&
    String(discountOverride).trim() !== ''
      ? toNumber(discountOverride)
      : toNumber(invoice?.invoice_discount);

  const difference = Number((total - paymentAmount).toFixed(2));
  const remaining = difference > 0 ? difference : 0;
  const overpayment = difference < 0 ? Math.abs(difference) : 0;

  return {
    total,
    discount,
    net: paymentAmount,
    posted: paymentAmount,
    difference,
    remaining,
    overpayment,
  };
}

function getChildInvoiceBalance(invoice) {
  const balance = getAuthoritativeBalance(invoice);
  return Number.isFinite(balance) ? balance : 0;
}

function resolveDisplayScore(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return '';
  }

  if (parsed > 0 && parsed < 1) {
    return String(Math.round(parsed * 100));
  }

  return Number.isInteger(parsed) ? String(parsed) : String(Number(parsed.toFixed(2)));
}

function resolveCustomerId(invoice) {
  return trimStr(
    invoice?.mas_customer_id || invoice?.customer_id || invoice?.mas_id || invoice?.masid
  );
}

function resolvePaymentLabel(invoice) {
  // PMT DISPLAY RULE
  // --------------------------------------------------------------------------
  // The PMT field is only for the actual LOKA payment_number.
  // Never substitute MAS customer IDs, remittance IDs, or seed identifiers.
  // If there is no LOKA payment_number, the PMT slot remains blank.
  // --------------------------------------------------------------------------
  const paymentNumber = trimStr(invoice?.payment_number);

  return paymentNumber ? `PMT ${paymentNumber}` : 'PMT -';
}

// ============================================================================
// BLOCK BP.UTIL.003 — Child row text builders
// -----------------------------------------------------------------------------
// PURPOSE
// - Produce the primary and secondary labels shown under assigned child rows.
// - Keep the formatting consistent across Bank Posting tree and invoice list views.
// ============================================================================

function buildChildInvoicePrimary(invoice) {
  const invoiceKind = resolveChildInvoiceKind(invoice);
  const invoiceNumber =
    invoiceKind === 'suggested'
      ? trimStr(invoice?.masio_invoice_number || invoice?.invoice_number)
      : trimStr(invoice?.invoice_number);
  const invoiceDate = formatDate(invoice?.invoice_date || invoice?.due_date || '');
  const customerId = resolveCustomerId(invoice);
  const customerName =
    invoiceKind === 'suggested'
      ? trimStr(invoice?.bill_to_name || invoice?.customer_name)
      : trimStr(invoice?.customer_name || invoice?.bill_to_name);

  const paymentLabel = resolvePaymentLabel(invoice);

  if (invoiceKind === 'suggested') {
    const { total, net, remaining, overpayment } = resolveChildInvoiceAmounts(invoice);
    const amountSegment = Number.isFinite(getAuthoritativeBalance(invoice))
      ? overpayment > 0.005
        ? `${money(total)} (${money(net)} Payment - ${money(overpayment)} Overpayment)`
        : `${money(total)} (${money(net)} - ${money(remaining)})`
      : 'Balance unavailable';
    const customerSegment = [customerId, customerName].filter(Boolean).join(' - ');

    return `${amountSegment} - ${invoiceNumber} - ${paymentLabel} - ${invoiceDate}${customerSegment ? ` - ${customerSegment}` : ''}`;
  }

  const balance = hasAuthoritativeBalance(invoice)
    ? money(getChildInvoiceBalance(invoice))
    : 'Balance unavailable';
  const description = customerName || '-';
  const customerSegment = [customerId, description].filter(Boolean).join(' - ');

  return `${balance} - ${invoiceNumber} - ${paymentLabel} - ${invoiceDate}${customerSegment ? ` - ${customerSegment}` : ''}`;
}

function buildChildInvoiceSecondary(invoice) {
  const invoiceKind = resolveChildInvoiceKind(invoice);
  const rawReason = trimStr(
    invoice?.match_reason || invoice?.match_origin || invoice?.source || invoice?.invoice_source
  ).toLowerCase();

  if (invoiceKind === 'suggested') {
    const score = resolveDisplayScore(
      invoice?.score ?? invoice?.match_score ?? invoice?.confidence_score
    );
    const name = normalizeCustomerName(invoice?.bill_to_name || invoice?.customer_name);
    const customerId = resolveCustomerId(invoice);
    const details = [customerId, name].filter(Boolean).join(' - ');
    const label = `LOKA Suggested${score ? ` | Score: ${score}` : ''}`;

    return `${label}${details ? ` - ${details}` : ''}`;
  }

  if (!rawReason || rawReason === 'open' || rawReason === 'open-manual-placement') {
    return 'Open invoice';
  }

  return trimStr(invoice?.match_reason || invoice?.match_origin || 'Open invoice');
}

// ============================================================================
// BLOCK BP.UTIL.004 — Suggestion list formatting helpers
// -----------------------------------------------------------------------------
// PURPOSE
// - Format invoice and suggestion rows in the Suggested/Open invoice list panels.
// - Mirror the same normalization logic used by the child rows without duplicating
//   logic in multiple components.
// ============================================================================

function getInvoiceBalance(row) {
  const balance = getAuthoritativeBalance(row);
  return Number.isFinite(balance) ? balance : 0;
}

function getInvoiceDate(row) {
  return row?.invoice_date || row?.invoiceDate || row?.date || row?.due_date || '';
}

function resolveInvoiceAmounts(row) {
  const total = getInvoiceBalance(row);
  const paymentAmount = Number(
    row?.applied_amount_override ??
      row?.payment_amount ??
      row?.applied_amount ??
      row?.amount_applied ??
      row?.invoice_amount
  );
  const discountOverride = row?.invoice_discount_override;
  const discount =
    discountOverride !== undefined &&
    discountOverride !== null &&
    String(discountOverride).trim() !== ''
      ? toNumber(discountOverride)
      : toNumber(row?.invoice_discount ?? 0);
  const difference = Number((total - paymentAmount).toFixed(2));
  const remaining = difference > 0 ? difference : 0;
  const overpayment = difference < 0 ? Math.abs(difference) : 0;

  return {
    total,
    discount,
    net: paymentAmount,
    remaining,
    overpayment,
  };
}

function resolveMasid(row) {
  return trimStr(row?.masid || row?.mas_id || row?.payment_id || row?.remittance_id);
}

function resolveCustomerLabel(row) {
  return normalizeCustomerName(row?.customer_name || '');
}

function resolveMasCustomerId(row) {
  return trimStr(row?.mas_customer_id || resolveMasid(row));
}

function resolveBillToName(row) {
  return normalizeCustomerName(row?.bill_to_name || row?.customer_name || '');
}

function buildSuggestedSecondaryDetails(row) {
  const masCustomerId = resolveMasCustomerId(row);
  const billToName = resolveBillToName(row);

  return [masCustomerId, billToName].filter(Boolean).join(' - ');
}

function resolveSuggestionOrigin(row) {
  const explicit = trimStr(row?.suggestion_origin).toLowerCase();
  if (explicit) return explicit;

  const source = trimStr(row?.invoice_source || row?.source).toLowerCase();
  return source.startsWith('sage') ? 'open-fallback' : 'loka-suggested';
}

function resolveSuggestionOriginLabel(row) {
  const explicit = trimStr(row?.suggestion_origin_label);
  if (explicit) return explicit;

  const origin = resolveSuggestionOrigin(row);
  if (origin === 'open-fallback') return 'Open fallback';
  if (origin === 'loka-weak') return 'LOKA Weak Match';
  return 'LOKA Suggested';
}

function buildInvoicePrimary(row, kind = 'generic') {
  const balance = hasAuthoritativeBalance(row)
    ? money(getInvoiceBalance(row))
    : 'Balance unavailable';
  const invoiceNumber = row?.invoice_number || '-';
  const paymentNumber = trimStr(row?.payment_number);
  const invoiceDate = formatDate(getInvoiceDate(row)) || '-';
  const customerName = resolveCustomerLabel(row) || '-';
  const prefix = row?.is_assigned_elsewhere ? '- ' : '';
  const paymentLabel = paymentNumber ? `PMT ${paymentNumber}` : 'PMT -';

  if (kind === 'suggested') {
    const { total, net, remaining, overpayment } = resolveInvoiceAmounts(row);
    const amountSegment = hasAuthoritativeBalance(row)
      ? overpayment > 0.005
        ? `${money(total)} (${money(net)} Payment - ${money(overpayment)} Overpayment)`
        : `${money(total)} (${money(net)} - ${money(remaining)})`
      : 'Balance unavailable';

    return `${prefix}${amountSegment} - ${invoiceNumber} - ${paymentLabel} - ${invoiceDate}`;
  }

  return `${prefix}${balance} - ${invoiceNumber} - ${paymentLabel} - ${invoiceDate} - ${customerName}`;
}

export {
  buildChildInvoicePrimary,
  buildChildInvoiceSecondary,
  buildInvoicePrimary,
  buildSuggestedSecondaryDetails,
  formatDate,
  getChildInvoiceBalance,
  money,
  normalizeCustomerName,
  resolveChildInvoiceKind,
  resolveSuggestionOrigin,
  resolveSuggestionOriginLabel,
  trimStr,
};
